import { useEffect, useRef, useState } from 'react';
import type {
  PDFDocumentLoadingTask,
  PDFDocumentProxy,
  PDFPageProxy,
  RenderTask,
} from 'pdfjs-dist';
import pdfWorkerUrl from 'pdfjs-dist/build/pdf.worker.min.mjs?url';
import { LoadingState } from '@/components/Spinner';

export type PdfLoadState = 'loading' | 'loaded' | 'error';

interface PdfCanvasViewerProps {
  url: string;
  title: string;
  /** 表示できたかを親に伝える。承認のように「中身を見たこと」が前提の操作で使う。 */
  onLoadStateChange?: (state: PdfLoadState) => void;
}

interface PdfCanvasPageProps {
  document: PDFDocumentProxy;
  pageNumber: number;
  availableWidth: number;
  availableHeight: number;
}

const DEFAULT_VIEWER_WIDTH = 960;
const DEFAULT_VIEWER_HEIGHT = 800;
const VIEWER_INSET = 48;
const MAX_DEVICE_PIXEL_RATIO = 2;

function PdfCanvasPage({
  document,
  pageNumber,
  availableWidth,
  availableHeight,
}: PdfCanvasPageProps) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    let renderTask: RenderTask | null = null;

    const renderPage = async () => {
      try {
        const page: PDFPageProxy = await document.getPage(pageNumber);
        if (cancelled || !canvasRef.current) return;

        const baseViewport = page.getViewport({ scale: 1 });
        const widthScale = Math.max(1, availableWidth - VIEWER_INSET) / baseViewport.width;
        const heightScale = Math.max(1, availableHeight - VIEWER_INSET) / baseViewport.height;
        const cssScale = Math.min(widthScale, heightScale, 1.5);
        const pixelRatio = Math.min(window.devicePixelRatio || 1, MAX_DEVICE_PIXEL_RATIO);
        const renderViewport = page.getViewport({ scale: cssScale * pixelRatio });
        const canvas = canvasRef.current;

        canvas.width = Math.ceil(renderViewport.width);
        canvas.height = Math.ceil(renderViewport.height);
        canvas.style.width = `${Math.ceil(renderViewport.width / pixelRatio)}px`;
        canvas.style.height = `${Math.ceil(renderViewport.height / pixelRatio)}px`;

        renderTask = page.render({ canvas, viewport: renderViewport });
        await renderTask.promise;
        if (!cancelled) setError(null);
      } catch (renderError) {
        if (!cancelled && (renderError as { name?: string }).name !== 'RenderingCancelledException') {
          setError('このページを表示できませんでした');
        }
      }
    };

    void renderPage();
    return () => {
      cancelled = true;
      renderTask?.cancel();
    };
  }, [availableHeight, availableWidth, document, pageNumber]);

  return (
    <div
      aria-label={`${pageNumber}ページ目`}
      className="flex min-h-32 justify-center rounded bg-gray-100 p-2 shadow-sm"
    >
      {error ? (
        <p className="self-center text-sm text-red-700">{error}</p>
      ) : (
        <canvas ref={canvasRef} className="block max-w-full bg-white" />
      )}
    </div>
  );
}

export function PdfCanvasViewer({ url, title, onLoadStateChange }: PdfCanvasViewerProps) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [document, setDocument] = useState<PDFDocumentProxy | null>(null);
  const [availableWidth, setAvailableWidth] = useState(DEFAULT_VIEWER_WIDTH);
  const [availableHeight, setAvailableHeight] = useState(DEFAULT_VIEWER_HEIGHT);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return undefined;

    const updateSize = () => {
      setAvailableWidth(container.clientWidth || DEFAULT_VIEWER_WIDTH);
      setAvailableHeight(container.clientHeight || DEFAULT_VIEWER_HEIGHT);
    };
    updateSize();

    if (typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(updateSize);
    observer.observe(container);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    let cancelled = false;
    let loadingTask: PDFDocumentLoadingTask | null = null;

    setDocument(null);
    setError(null);
    import('pdfjs-dist')
      .then((pdfjs) => {
        if (cancelled) return null;
        pdfjs.GlobalWorkerOptions.workerSrc = pdfWorkerUrl;
        loadingTask = pdfjs.getDocument({ url });
        return loadingTask.promise;
      })
      .then((loadedDocument) => {
        if (!loadedDocument) return;
        if (cancelled) {
          void loadedDocument.destroy();
          return;
        }
        setDocument(loadedDocument);
      })
      .catch(() => {
        if (!cancelled) setError('PDFを表示できませんでした');
      });

    return () => {
      cancelled = true;
      if (loadingTask) void loadingTask.destroy();
    };
  }, [url]);

  useEffect(() => {
    if (!onLoadStateChange) return;
    onLoadStateChange(error ? 'error' : document ? 'loaded' : 'loading');
  }, [document, error, onLoadStateChange]);

  return (
    <div
      ref={containerRef}
      data-testid="overall-analysis-pdf-canvas-viewer"
      role="region"
      aria-label={title}
      className="h-full w-full overflow-auto bg-gray-200 p-3"
    >
      {error && <p className="p-6 text-center text-sm text-red-700">{error}</p>}
      {!error && !document && (
        <div className="flex h-full items-center justify-center">
          <LoadingState message="PDFを描画中..." />
        </div>
      )}
      {document && (
        <div className="mx-auto grid w-full gap-4">
          {Array.from({ length: document.numPages }, (_, index) => (
            <PdfCanvasPage
              key={index + 1}
              document={document}
              pageNumber={index + 1}
              availableWidth={availableWidth}
              availableHeight={availableHeight}
            />
          ))}
        </div>
      )}
    </div>
  );
}
